import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withPolaris } from 'Components/protons/Polaris';
import { debounce } from 'lodash';

import './_style.scss';

const JumpToNavigation = ({
  context: { cssPrefix },
  extraClassNames,
  sections,
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [activeHash, setActiveHash] = useState('');
  const navRef = useRef(null);
  const sectionRefs = useRef({});
  const initialHashHandled = useRef(false);

  // Function to scroll to the target section.
  const scrollToSection = (hash, navHeight) => {
    const targetElement = sectionRefs.current[hash];

    if (targetElement) {
      const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
      // Adjust the target position to account for the sticky navigation height
      // and an additional 16px offset for better visibility
      const adjustedPosition = targetPosition - navHeight - 16;
      window.scrollTo({
        top: adjustedPosition,
        behavior: 'smooth',
      });
    }
  };

  // Handle the nav click.
  const handleNavClick = (hash, navHeight) => {
    setActiveHash(hash);
    setTimeout(() => {
      scrollToSection(hash, navHeight);
    }, 10); // Delay to ensure DOM is ready
  };

  // Function to get the top position of the first section.
  const getFirstSectionTop = () => {
    const firstSectionHash = Object.keys(sectionRefs.current)[0];
    if (!firstSectionHash) return null;

    const firstSection = document.querySelector(firstSectionHash);
    if (!firstSection) return null;

    return firstSection.getBoundingClientRect().top + window.pageYOffset;
  };

  // Function to get the active section based on scroll position.
  const getActiveSection = (currentScrollPos, navHeight) => {
    const sectionHashes = Object.keys(sectionRefs.current);

    if (sectionHashes.length === 0) {
      return null;
    }

    // Ensure there is no hash in the URL if the user scrolls above the first section.
    const firstSectionTop = getFirstSectionTop();
    if (firstSectionTop) {
      // Add a 16px offset for better visibility and a 5px threshold
      // so that the correct button has the active state.
      if (currentScrollPos + navHeight + 16 + 5 < firstSectionTop) {
        return '';
      }
    }

    return sectionHashes.findLast((hash) => {
      const section = sectionRefs.current[hash];
      if (!section) return '';

      // Dynamically calculate the section's position relative to the top of the page.
      const sectionTop = section.getBoundingClientRect().top + window.pageYOffset;

      // This is to stop the last has being temporarily active.
      if (sectionTop === currentScrollPos) return '';

      // Adjust the condition to account for the sticky navigation height,
      // an additional 16px offset for better visibility,
      // and a 5px threshold so that the correct button is having the active state.
      return sectionTop <= currentScrollPos + navHeight + 16 + 5;
    });
  };

  // Debounced scroll handler.
  const handleScroll = useCallback(
    debounce(() => {
      if (navRef.current) {
        const navOffsetTop = navRef.current.offsetTop;
        const currentScrollPos = window.pageYOffset;
        const navHeight = navRef.current.offsetHeight;
        setIsSticky(currentScrollPos >= navOffsetTop);

        const newActiveHash = getActiveSection(currentScrollPos, navHeight);

        if (newActiveHash !== activeHash) {
          setActiveHash(newActiveHash);
        }
      }
    }, 100),
    [activeHash],
  );

  // Set up the sectionRefs and add scroll event listener.
  useEffect(() => {
    sections?.forEach((section) => {
      sectionRefs.current[`#${section.id}`] = document.querySelector(
        `#${section.id}`,
      );
    });

    window.addEventListener('scroll', handleScroll);

    if (navRef.current && !initialHashHandled.current) {
      const navHeight = navRef.current.offsetHeight;
      if (window.location.hash) {
        handleNavClick(window.location.hash, navHeight);
      }
      initialHashHandled.current = true;
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      handleScroll.cancel();
    };
  }, [handleScroll, sections]);

  // Update the URL hash when activeHash changes.
  useEffect(() => {
    if (!activeHash) {
      // Remove the hash from the URL if activeHash is empty.
      if (window.history.pushState) {
        window.history.pushState(null, null, window.location.pathname);
      } else {
        window.location.hash = '';
      }
      return;
    }

    if (window.history.pushState) {
      window.history.pushState(null, null, activeHash);
    } else {
      window.location.hash = activeHash;
    }
  }, [activeHash]);

  // Ensure that the active link in the navigation is in the viewport.
  useEffect(() => {
    if (activeHash && navRef.current) {
      const targetElement = navRef.current.querySelector(`a[href="${activeHash}"]`);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [activeHash]);

  if (
    !sections
    || sections.length === 0
    || (sections.length === 1 && Object.keys(sections[0]).length === 0)
  ) {
    return null;
  }

  return (
    <nav
      ref={navRef}
      className={classNames({
        [`${cssPrefix}__jump-to-navigation`]: true,
        'is-sticky': isSticky,
        ...extraClassNames,
      })}
    >
      <ul>
        {sections.map((section) => section?.id && section?.text && (
          <li
            key={section.id}
            className={classNames({
              '-is-active': activeHash === `#${section.id}`,
            })}
          >
            <a
              href={`#${section.id}`}
              onClick={(event) => {
                event.preventDefault();
                handleNavClick(`#${section.id}`, navRef.current.offsetHeight);
              }}
            >
              {section.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

JumpToNavigation.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string.isRequired,
  }).isRequired,
  extraClassNames: PropTypes.shape(),
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
    }),
  ),
};

JumpToNavigation.defaultProps = {
  extraClassNames: {},
  sections: [],
};

export default withPolaris(JumpToNavigation);
